import {type AnimationEvent, type AnimationEventHandler, useCallback, useSyncExternalStore} from 'react'

import {useBoardLanding} from '../provider/contexts'
import type {BoardCardAddress} from '../types'

export type BoardCardLanding = {
  hasLanded: boolean
  onAnimationEnd: AnimationEventHandler<HTMLElement>
}

/**
 * Whether this card is where the last reported move landed, and the handler that ends the
 * landing once the card has shown it.
 *
 * `card` must be stable across renders, because it keys the subscription.
 */
export function useBoardCardLanding(card: BoardCardAddress): BoardCardLanding {
  const {subscribe, hasLanded: isLandedCard, end} = useBoardLanding()

  const getSnapshot = useCallback(() => isLandedCard(card), [isLandedCard, card])
  const hasLanded = useSyncExternalStore(subscribe, getSnapshot, nothingHasLanded)

  const onAnimationEnd = useCallback(
    (event: AnimationEvent<HTMLElement>) => {
      // An animation the caller drew inside the card ends here too, and says nothing about
      // the card itself.
      if (event.target === event.currentTarget) end(card)
    },
    [end, card],
  )

  return {hasLanded, onAnimationEnd}
}

function nothingHasLanded(): boolean {
  return false
}
